import React, { useState, useEffect, useCallback } from 'react';
import './CommonStyles.css';
import { Info, Download, ChevronDown, Plus, Search, Trash2, Edit3, Printer, FileSpreadsheet, FileText, LayoutGrid } from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

const TaxRates = () => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAddGroupModal, setShowAddGroupModal] = useState(false);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showGroupExportDropdown, setShowGroupExportDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [groupSearchQuery, setGroupSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingRateId, setEditingRateId] = useState(null);
    const [editingGroupId, setEditingGroupId] = useState(null);

    const [taxRates, setTaxRates] = useState([]);
    const [taxGroups, setTaxGroups] = useState([]);

    const defaultRateForm = { name: '', amount: '', for_tax_group: false };
    const [rateForm, setRateForm] = useState({ ...defaultRateForm });

    const defaultGroupForm = { name: '', taxes: [] };
    const [groupForm, setGroupForm] = useState({ ...defaultGroupForm });

    const fetchTaxRates = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.getTaxRates();
            if (res.success && Array.isArray(res.data)) {
                setTaxRates(res.data.map(r => ({
                    id: r.id,
                    name: r.for_tax_group ? `${r.name} (For tax group only)` : r.name,
                    rate: parseFloat(r.amount).toFixed(2),
                    raw: r
                })));
            } else {
                setTaxRates([]);
            }
        } catch (err) {
      /* console.error('Error fetching tax rates:', err); */
            setTaxRates([]);
        }
        setLoading(false);
    }, []);

    const fetchTaxGroups = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.getTaxGroups();
            if (res.success && Array.isArray(res.data)) {
                setTaxGroups(res.data.map(g => ({
                    id: g.id,
                    name: g.name,
                    subTaxes: g.sub_taxes || '',
                    raw: g
                })));
            } else {
                setTaxGroups([]);
            }
        } catch (err) {
      /* console.error('Error fetching tax groups:', err); */
            setTaxGroups([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchTaxRates();
        fetchTaxGroups();
    }, [fetchTaxRates, fetchTaxGroups]);

    const handleOpenAddRate = () => {
        setEditingRateId(null);
        setRateForm({ ...defaultRateForm });
        setShowAddModal(true);
    };

    const handleOpenEditRate = async (rate) => {
        try {
            const res = await apiService.getTaxRate(rate.id);
            if (res.success && res.data) {
                const r = res.data;
                setRateForm({
                    name: r.name || '',
                    amount: r.amount != null ? String(r.amount) : '',
                    for_tax_group: !!r.for_tax_group
                });
                setEditingRateId(rate.id);
                setShowAddModal(true);
            }
        } catch (err) {
            toast.error('Failed to load tax rate');
        }
    };

    const handleSaveRate = async () => {
        if (!rateForm.name) { toast.error('Name is required'); return; }
        if (rateForm.amount === '' || rateForm.amount == null) { toast.error('Tax Rate % is required'); return; }
        const payload = {
            name: rateForm.name,
            amount: rateForm.amount,
            for_tax_group: rateForm.for_tax_group ? 1 : 0
        };
        try {
            if (editingRateId) {
                await apiService.updateTaxRate(editingRateId, payload);
                toast.success('Tax rate updated successfully');
            } else {
                await apiService.createTaxRate(payload);
                toast.success('Tax rate added successfully');
            }
            setShowAddModal(false);
            fetchTaxRates();
        } catch (err) {
            toast.error(err.message || 'Failed to save tax rate');
        }
    };

    const handleDeleteRate = async (id) => {
        if (!window.confirm('Are you sure you want to delete this tax rate?')) return;
        try {
            await apiService.deleteTaxRate(id);
            toast.success('Tax rate deleted successfully');
            fetchTaxRates();
        } catch (err) {
            toast.error(err.message || 'Failed to delete tax rate');
        }
    };

    const handleOpenAddGroup = () => {
        setEditingGroupId(null);
        setGroupForm({ ...defaultGroupForm });
        setShowAddGroupModal(true);
    };

    const handleOpenEditGroup = async (group) => {
        try {
            const res = await apiService.getTaxGroup(group.id);
            if (res.success && res.data && res.data.tax_group) {
                const g = res.data.tax_group;
                setGroupForm({
                    name: g.name || '',
                    taxes: res.data.sub_tax_ids || []
                });
                setEditingGroupId(group.id);
                setShowAddGroupModal(true);
            }
        } catch (err) {
            toast.error('Failed to load tax group');
        }
    };

    const handleSaveGroup = async () => {
        if (!groupForm.name) { toast.error('Name is required'); return; }
        if (!groupForm.taxes || groupForm.taxes.length === 0) { toast.error('At least one sub tax is required'); return; }
        const payload = {
            name: groupForm.name,
            taxes: groupForm.taxes
        };
        try {
            if (editingGroupId) {
                await apiService.updateTaxGroup(editingGroupId, payload);
                toast.success('Tax group updated successfully');
            } else {
                await apiService.createTaxGroup(payload);
                toast.success('Tax group added successfully');
            }
            setShowAddGroupModal(false);
            fetchTaxGroups();
        } catch (err) {
            toast.error(err.message || 'Failed to save tax group');
        }
    };

    const handleDeleteGroup = async (id) => {
        if (!window.confirm('Are you sure you want to delete this tax group?')) return;
        try {
            await apiService.deleteTaxGroup(id);
            toast.success('Tax group deleted successfully');
            fetchTaxGroups();
        } catch (err) {
            toast.error(err.message || 'Failed to delete tax group');
        }
    };

    const filteredTaxRates = taxRates.filter(r =>
        r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.rate?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredTaxGroups = taxGroups.filter(g =>
        g.name?.toLowerCase().includes(groupSearchQuery.toLowerCase()) ||
        g.subTaxes?.toLowerCase().includes(groupSearchQuery.toLowerCase())
    );

    const toggleSubTax = (taxId) => {
        setGroupForm(f => {
            const taxes = f.taxes.includes(taxId)
                ? f.taxes.filter(id => id !== taxId)
                : [...f.taxes, taxId];
            return { ...f, taxes };
        });
    };

    const renderAddModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', width: '600px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{editingRateId ? 'Edit Tax Rate' : 'Add Tax Rate'}</h3>
                    <button onClick={() => { setShowAddModal(false); setEditingRateId(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Name:*</label>
                            <input type="text" placeholder="Name" value={rateForm.name} onChange={(e) => setRateForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                        </div>

                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>
                                Tax Rate %:* <Info size={16} style={{ color: '#00d1ff' }} />
                            </label>
                            <input type="text" placeholder="" value={rateForm.amount} onChange={(e) => setRateForm(f => ({ ...f, amount: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="checkbox" id="taxGroupOnly" checked={rateForm.for_tax_group} onChange={(e) => setRateForm(f => ({ ...f, for_tax_group: e.target.checked }))} />
                            <label htmlFor="taxGroupOnly" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>
                                For tax group only <Info size={16} style={{ color: '#00d1ff' }} />
                            </label>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div style={{ padding: '1.5rem', borderTop: 'none', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    <button onClick={handleSaveRate} style={{ background: '#4c00ff', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '90px' }}>
                        Save
                    </button>
                    <button onClick={() => { setShowAddModal(false); setEditingRateId(null); }} style={{ background: '#2d3748', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '90px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAddGroupModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', width: '500px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{editingGroupId ? 'Edit tax group' : 'Add tax group'}</h3>
                    <button onClick={() => { setShowAddGroupModal(false); setEditingGroupId(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Name:*</label>
                            <input type="text" placeholder="Name" value={groupForm.name} onChange={(e) => setGroupForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Sub taxes:*</label>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', padding: '0.5rem' }}>
                                {taxRates.map((tax) => (
                                    <div key={tax.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0' }}>
                                        <input type="checkbox" id={`tax-${tax.id}`} checked={groupForm.taxes.includes(tax.id)} onChange={() => toggleSubTax(tax.id)} />
                                        <label htmlFor={`tax-${tax.id}`} style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #4a5568)' }}>{tax.raw.name} ({tax.rate}%)</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div style={{ padding: '1.5rem', borderTop: 'none', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    <button onClick={handleSaveGroup} style={{ background: '#4c00ff', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '90px' }}>
                        Save
                    </button>
                    <button onClick={() => { setShowAddGroupModal(false); setEditingGroupId(null); }} style={{ background: '#2d3748', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '90px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ padding: '0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Tax Rates</h2>
                <span style={{ color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.9rem' }}>Manage your tax rates</span>
            </div>

            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {/* Header Row */}
                <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1.25rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>All your tax rates</h3>
                    <button
                        onClick={handleOpenAddRate}
                        style={{ marginLeft: 'auto', background: '#4c00ff', color: 'white', border: 'none', borderRadius: '20px', padding: '0.5rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                    >
                        <Plus size={18} /> Add
                    </button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Controls Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Show</span>
                            <select style={{ padding: '0.3rem 0.5rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.85rem' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>entries</span>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                                    style={{ background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #64748b)', border: '1px solid var(--etpos-border, #e2e8f0)', padding: '0.5rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
                                >
                                    <Download size={16} /> Export <ChevronDown size={14} />
                                </button>
                                {showExportDropdown && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '180px', padding: '0.5rem 0' }}>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#10b981" /> Export CSV
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#3b82f6" /> Export Excel
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <Printer size={16} color="#6366f1" /> Print
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <LayoutGrid size={16} color="#8b5cf6" /> Column visibility
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileText size={16} color="#ef4444" /> Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Search:</span>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.85rem', outline: 'none' }}
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderTop: '1px solid var(--etpos-border, #e2e8f0)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            Name
                                            <div style={{ display: 'flex', flexDirection: 'column', fontSize: '10px' }}>▲▼</div>
                                        </div>
                                    </th>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            Tax Rate %
                                            <div style={{ display: 'flex', flexDirection: 'column', fontSize: '10px' }}>▲▼</div>
                                        </div>
                                    </th>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTaxRates.map((rate) => (
                                    <tr key={rate.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                                        <td style={{ padding: '1rem', color: 'var(--etpos-text-muted, #4a5568)' }}>{rate.name}</td>
                                        <td style={{ padding: '1rem', color: 'var(--etpos-text-muted, #4a5568)' }}>{rate.rate}</td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => handleOpenEditRate(rate)} style={{ background: 'var(--etpos-card, #fff)', color: '#6200ea', border: '1px solid #6200ea', padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600', cursor: 'pointer' }}>
                                                    <Edit3 size={14} /> Edit
                                                </button>
                                                <button onClick={() => handleDeleteRate(rate.id)} style={{ background: 'var(--etpos-card, #fff)', color: '#ff5252', border: '1px solid #ff5252', padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600', cursor: 'pointer' }}>
                                                    <Trash2 size={14} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #4a5568)' }}>Showing 1 to 6 of 6 entries</span>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-card, #fff)', borderRadius: '4px 0 0 4px', fontSize: '0.85rem', color: 'var(--etpos-text-muted, #a0aec0)', cursor: 'not-allowed' }}>Previous</button>
                            <button style={{ padding: '0.5rem 1rem', border: '1px solid #4a5568', background: '#4a5568', color: '#fff', fontSize: '0.85rem' }}>1</button>
                            <button style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', background: 'var(--etpos-card, #fff)', borderRadius: '0 4px 4px 0', fontSize: '0.85rem', color: 'var(--etpos-text-muted, #a0aec0)', cursor: 'not-allowed' }}>Next</button>
                        </div>
                    </div>
                </div>

                {showAddModal && renderAddModal()}
            </div>

            {/* Tax Groups Section */}
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginTop: '1rem' }}>
                {/* Header Row */}
                <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1.25rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>All your tax groups</h3>
                    <button
                        onClick={handleOpenAddGroup}
                        style={{ marginLeft: 'auto', background: '#4c00ff', color: 'white', border: 'none', borderRadius: '20px', padding: '0.5rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                    >
                        <Plus size={18} /> Add
                    </button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Controls Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Show</span>
                            <select style={{ padding: '0.3rem 0.5rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.85rem' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>entries</span>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowGroupExportDropdown(!showGroupExportDropdown)}
                                    style={{ background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #64748b)', border: '1px solid var(--etpos-border, #e2e8f0)', padding: '0.5rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
                                >
                                    <Download size={16} /> Export <ChevronDown size={14} />
                                </button>
                                {showGroupExportDropdown && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '180px', padding: '0.5rem 0' }}>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#10b981" /> Export CSV
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <Printer size={16} color="#6366f1" /> Print
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileText size={16} color="#ef4444" /> Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Search:</span>
                            <input
                                type="text"
                                value={groupSearchQuery}
                                onChange={(e) => setGroupSearchQuery(e.target.value)}
                                style={{ padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.85rem', outline: 'none' }}
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderTop: '1px solid var(--etpos-border, #e2e8f0)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            Name
                                            <div style={{ display: 'flex', flexDirection: 'column', fontSize: '10px' }}>▲▼</div>
                                        </div>
                                    </th>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>Sub taxes</th>
                                    <th style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTaxGroups.map((group) => (
                                    <tr key={group.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                                        <td style={{ padding: '1rem', color: 'var(--etpos-text-muted, #4a5568)' }}>{group.name}</td>
                                        <td style={{ padding: '1rem', color: 'var(--etpos-text-muted, #4a5568)' }}>{group.subTaxes}</td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => handleOpenEditGroup(group)} style={{ background: 'var(--etpos-card, #fff)', color: '#6200ea', border: '1px solid #6200ea', padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600', cursor: 'pointer' }}>
                                                    <Edit3 size={14} /> Edit
                                                </button>
                                                <button onClick={() => handleDeleteGroup(group.id)} style={{ background: 'var(--etpos-card, #fff)', color: '#ff5252', border: '1px solid #ff5252', padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600', cursor: 'pointer' }}>
                                                    <Trash2 size={14} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {showAddGroupModal && renderAddGroupModal()}
            </div>
        </div>
    );
};

export default TaxRates;
